import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { fetchStores } from "../store/actions/storeActions";
import { getAllCoupons } from "../store/actions/couponActions";
import { getCategories } from "../store/actions/categoryActions";
import { Container } from "../components/layout/Container";
import SearchEmptyState from "../components/search/SearchEmptyState";
import SearchResultsSections from "../components/search/SearchResultsSections";
import useSEO from "../hooks/useSEO";
import { trackSearch } from "../utils/analytics";

const Main = styled.div`
  padding: 2rem 0;
`;

const Head = styled.div`
  margin-bottom: 2rem;
`;

const H1 = styled.h1`
  font-size: clamp(1.5rem, 1.15rem + 1.4vw, 2.125rem);
  font-weight: 700;
  margin: 0 0 0.25rem;
  color: hsl(${({ theme }) => theme.colors.foreground});
`;

const Meta = styled.p`
  margin: 0;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const LoadNote = styled.p`
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  margin-bottom: 1rem;
`;

export default function SearchResultsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const raw = searchParams.get("q") || "";
  const q = raw.toLowerCase();

  const stores = useSelector((s) => s.store.stores) || [];
  const reduxCoupons = useSelector((s) => s.coupon.coupons) || [];
  const categories = useSelector((s) => s.category.categories) || [];
  const storeLoading = useSelector((s) => s.store.loading);
  const couponLoading = useSelector((s) => s.coupon.loading);

  useEffect(() => {
    dispatch(fetchStores());
    dispatch(getAllCoupons());
    dispatch(getCategories());
  }, [dispatch]);

  const loading = storeLoading || couponLoading;

  const matchedStores = useMemo(
    () => stores.filter((s) => s.name.toLowerCase().includes(q)),
    [stores, q]
  );
  const matchedCoupons = useMemo(
    () =>
      reduxCoupons.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.storeName.toLowerCase().includes(q) ||
          (c.description && c.description.toLowerCase().includes(q))
      ),
    [reduxCoupons, q]
  );
  const matchedCategories = useMemo(
    () =>
      categories.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.description && c.description.toLowerCase().includes(q))
      ),
    [categories, q]
  );

  const total =
    matchedStores.length + matchedCoupons.length + matchedCategories.length;

  useEffect(() => {
    if (!raw) return;
    trackSearch({ query: raw, resultCount: total });
  }, [raw, total]);

  const seo = useSEO({
    title: raw ? `Search: ${raw}` : "Search",
    description: raw
      ? `Find coupons, stores, and categories matching "${raw}".`
      : "Search coupons and deals.",
    noindex: true,
    nofollow: false,
  });

  return (
    <Main>
      {seo}
      <Container>
        <Head>
          <H1>
            Search results for &quot;
            <span style={{ color: "hsl(221 67% 44%)" }}>{raw}</span>&quot;
          </H1>
          <Meta>
            {total} result{total !== 1 ? "s" : ""} found
          </Meta>
        </Head>
        {loading && <LoadNote>{t("search.refreshing")}</LoadNote>}
        {total === 0 && <SearchEmptyState />}
        {total > 0 && (
          <SearchResultsSections
            matchedStores={matchedStores}
            matchedCategories={matchedCategories}
            matchedCoupons={matchedCoupons}
          />
        )}
      </Container>
    </Main>
  );
}
